import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  FilterList,
  ArrowForward,
  KeyboardArrowDown,
  CategoryOutlined,
  KeyboardArrowRight,
} from '@mui/icons-material';

import Loading from '../../../components/global/loading/Loading';
import { useCategory } from '../../../context/CategoryContext';
import { Category } from '../../../models/Category';
import { useConfig } from '../../../utils/useConfig';

function Categories() {
  const { getCategories } = useCategory();
  const config = useConfig();
  const mainColor =
    config.theme.mainColor || config.theme.colors.primary || '#3B82F6';

  const [categories, setCategories] = useState<Category[] | null>(null);
  const [isMounted, setIsMounted] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const defaultCategoryCount = 6;
  const mobileDefaultCategoryCount = 4;

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    setIsMounted(true);
    async function fetchCategories() {
      try {
        const response = await getCategories();
        if (isMounted) {
          setCategories(response.categories);
        }
      } catch (e) {
        console.error(e);
      }
    }

    fetchCategories();

    return () => {
      setIsMounted(false);
    };
  }, [getCategories, isMounted]);

  if (!categories) {
    return (
      <div className="flex justify-center items-center h-40">
        <Loading />
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-slate-200 h-10 w-10"></div>
          <div className="flex-1 space-y-6 py-1">
            <div className="h-2 bg-slate-200 rounded"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-2 bg-slate-200 rounded col-span-2"></div>
                <div className="h-2 bg-slate-200 rounded col-span-1"></div>
              </div>
              <div className="h-2 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
    added: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
    removed: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.3,
        ease: 'easeIn',
      },
    },
  };

  const hoverScale = {
    scale: 1.02,
    boxShadow:
      '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
    transition: { type: 'spring', stiffness: 400, damping: 17 },
  };

  const displayedCategories = showAllCategories
    ? categories
    : categories.slice(
        0,
        isMobile ? mobileDefaultCategoryCount : defaultCategoryCount,
      );

  const renderMobileCategories = () => {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        {displayedCategories.map((category, index) => (
          <motion.a
            key={category.id}
            href={`/szukaj?categoryId=${category.id}`}
            className={`flex items-center justify-between p-3 ${
              index !== displayedCategories.length - 1
                ? 'border-b border-gray-100'
                : ''
            }`}
            whileTap={{ backgroundColor: `${mainColor}10` }}
          >
            <div className="flex items-center space-x-3">
              {category.image ? (
                <div className="relative w-10 h-10 rounded-full overflow-hidden bg-gray-100">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLImageElement)
                        .parentElement;
                      if (parent) {
                        const iconElement = document.createElement('div');
                        iconElement.className =
                          'w-full h-full flex items-center justify-center';
                        iconElement.innerHTML =
                          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style="color:' +
                          mainColor +
                          ';width:1.25rem;height:1.25rem;"><path d="M4.5 12.75l7.5-7.5 7.5 7.5m-15 6l7.5-7.5 7.5 7.5"/></svg>';
                        parent.appendChild(iconElement);
                      }
                    }}
                  />
                </div>
              ) : (
                <div
                  className="p-2 rounded-full"
                  style={{ backgroundColor: `${mainColor}15` }}
                >
                  <CategoryOutlined
                    style={{ color: mainColor, fontSize: '1.25rem' }}
                  />
                </div>
              )}
              <div>
                <h3 className="font-medium text-gray-900">{category.name}</h3>
                {category.subCategories &&
                  category.subCategories.length > 0 && (
                    <p className="text-xs text-gray-500">
                      {category.subCategories.length} podkategorii
                    </p>
                  )}
              </div>
            </div>
            <KeyboardArrowRight fontSize="small" className="text-gray-400" />
          </motion.a>
        ))}

        {categories.length > mobileDefaultCategoryCount && (
          <motion.button
            onClick={() => setShowAllCategories(!showAllCategories)}
            className="w-full p-3 text-center flex items-center justify-center space-x-1 text-sm font-medium"
            style={{ color: mainColor }}
            whileTap={{ backgroundColor: `${mainColor}10` }}
          >
            <span>
              {showAllCategories
                ? 'Pokaż mniej kategorii'
                : 'Pokaż wszystkie kategorie'}
            </span>
            <motion.div
              animate={{ rotate: showAllCategories ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <KeyboardArrowDown fontSize="small" />
            </motion.div>
          </motion.button>
        )}
      </div>
    );
  };

  const renderDesktopCategories = () => {
    return (
      <>
        <motion.div
          key={`categories-container-${displayedCategories.length}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          style={{
            minHeight: showAllCategories ? 'auto' : '400px',
            transition: 'min-height 0.5s ease-in-out',
          }}
        >
          {displayedCategories.map((category) => {
            return (
              <motion.div
                key={category.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="removed"
                layout
                whileHover={hoverScale}
                onMouseEnter={() => setHoveredCategory(category.id)}
                onMouseLeave={() => setHoveredCategory(null)}
                className="rounded-xl overflow-hidden border border-gray-100 transition-all duration-300 relative group"
                style={{
                  aspectRatio: '3/2',
                  background: category.image
                    ? 'white'
                    : `linear-gradient(to bottom right, white, ${mainColor}10)`,
                }}
              >
                <a
                  href={`/szukaj?categoryId=${category.id}`}
                  className="block h-full"
                >
                  {category.image && (
                    <div className="absolute inset-0 w-full h-full opacity-10 group-hover:opacity-15 transition-opacity">
                      <img
                        src={category.image}
                        alt=""
                        className="w-full h-full object-cover object-center"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="p-6 h-full flex flex-col relative z-10">
                    <div className="flex justify-between items-start">
                      <div
                        className="mb-4 p-3 rounded-full transition-all duration-300 group-hover:scale-110 flex items-center justify-center"
                        style={{ backgroundColor: `${mainColor}15` }}
                      >
                        <CategoryOutlined
                          style={{ color: mainColor, fontSize: '2rem' }}
                        />
                      </div>
                      <motion.div
                        animate={{
                          rotate: hoveredCategory === category.id ? 0 : -45,
                        }}
                        transition={{ duration: 0.3 }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: mainColor }}
                      >
                        <ArrowForward />
                      </motion.div>
                    </div>

                    <div className="mt-auto">
                      <h3 className="font-bold text-xl text-gray-900 mb-2">
                        {category.name}
                      </h3>
                      {category.subCategories &&
                        category.subCategories.length > 0 && (
                          <div className="inline-flex items-center text-sm text-gray-600 bg-white bg-opacity-70 rounded-full px-3 py-1 mt-2">
                            <span>
                              {category.subCategories.length} podkategorii
                            </span>
                          </div>
                        )}
                    </div>
                  </div>

                  {category.image ? (
                    <div className="absolute bottom-3 right-3 w-16 h-16 rounded-md overflow-hidden border border-gray-200 shadow-sm opacity-80 group-hover:opacity-100 transition-opacity">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full opacity-5"
                        style={{ backgroundColor: mainColor }}
                      ></div>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-1/2 bg-white opacity-0 group-hover:opacity-5 rounded-full blur-3xl transition-all duration-500"></div>
                    </>
                  )}
                </a>
              </motion.div>
            );
          })}
        </motion.div>

        {categories.length > defaultCategoryCount && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex justify-center mt-10"
          >
            <motion.button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="px-6 py-3 bg-white border-2 border-gray-200 rounded-full flex items-center gap-2 text-gray-700 transition-all duration-300 group shadow-sm"
              whileHover={{
                y: -3,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                borderColor: mainColor,
                color: mainColor,
              }}
              whileTap={{ y: 0 }}
              style={{
                transition: 'border-color 0.3s, color 0.3s',
              }}
            >
              <span className="font-medium">
                {showAllCategories
                  ? 'Pokaż mniej kategorii'
                  : 'Pokaż wszystkie kategorie'}
              </span>
              <motion.div
                animate={{ rotate: showAllCategories ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                style={{ color: mainColor }}
              >
                <KeyboardArrowDown />
              </motion.div>
            </motion.button>
          </motion.div>
        )}
      </>
    );
  };

  return (
    <div
      className={`py-6 md:py-12 px-4`}
      style={{
        background: `linear-gradient(to bottom, #ffffff, ${mainColor}10)`,
      }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={titleVariants}
          className="text-center mb-8 md:mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-clip-text">
            Odkryj nasze kategorie
          </h2>
          <div
            className="mx-auto w-16 md:w-20 h-1.5 rounded-full mb-3 md:mb-4 bg-primary"
          ></div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
            Znajdź wszystko czego potrzebujesz w naszym bogatym asortymencie
            produktów elektrycznych
          </p>
        </motion.div>

        <div className="md:hidden">{renderMobileCategories()}</div>
        <div className="hidden md:block">{renderDesktopCategories()}</div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-10 md:mt-16 bg-white rounded-xl md:rounded-2xl p-5 md:p-8 border border-gray-100 shadow-md md:shadow-lg relative overflow-hidden"
        >
          <div
            className="absolute -top-16 -right-16 w-32 h-32 rounded-full opacity-5 hidden md:block"
            style={{ backgroundColor: mainColor }}
          ></div>
          <div
            className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full opacity-5 hidden md:block"
            style={{ backgroundColor: mainColor }}
          ></div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 md:gap-6 relative z-10">
            <div>
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-1 md:mb-2">
                Nie znalazłeś tego, czego szukasz?
              </h4>
              <p className="text-sm text-gray-600">
                Sprawdź nasz pełen katalog produktów lub skorzystaj z
                wyszukiwarki
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto">
              <motion.a
                href="/szukaj"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="px-4 md:px-5 py-2 md:py-3 bg-white border-2 border-gray-200 text-gray-800 rounded-lg md:rounded-xl text-xs md:text-sm font-medium flex items-center justify-center gap-2 hover:border-gray-300 transition-colors shadow-sm"
              >
                <FilterList fontSize="small" />
                Pełen katalog
              </motion.a>
              <motion.button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  const searchInput =
                    document.querySelector('input[type="text"]');
                  if (searchInput) {
                    setTimeout(() => {
                      (searchInput as HTMLInputElement).focus();
                    }, 500);
                  }
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className={`px-4 md:px-5 py-2 md:py-3 text-white rounded-lg md:rounded-xl text-xs md:text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-colors shadow-sm bg-primary`}
              >
                <Search fontSize="small" />
                Wyszukaj produkt
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Categories;
